// Multi-tenancy "borrow a seat" — localStorage bookkeeping shared by the
// SelectTenant picker and the acting-as banner.
//
// Keys involved:
//   token         — the active JWT (assumed tenant token while borrowing)
//   platformToken — stash of the super admin's own role-99 token
//   actingAs      — {tenantId, name, logo, roleName} for the banner
//   tenantId      — feeds the x-tenant header (see services/Request.js)
//   tenantConfig  — cached public branding; dropped on every seat change so
//                   the next loadTenantConfig() paints the right brand
//
// The seat-switch flows always end in a full-page window.location navigation,
// so redux re-initializes on landing; that is why these helpers persist to
// localStorage directly (mirroring what the login sagas store) instead of
// dispatching saga actions whose in-memory results the navigation would drop.
import { getRolesAndPermissionsService } from 'services/RolesAndPermissionsService';
import { profileApi } from 'services/SettingsService';
import { loadTenantConfig } from 'config/tenantConfig';
import { injectTheme } from 'config/themeInjector';
import { decodeJwt } from './jwtRole';

export const getActingAs = () => {
  try {
    const raw = localStorage.getItem('actingAs');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// Mirrors the `payload` branch of RolesAndPermissionsSaga: flatten module +
// subModule keys (plus the always-on settings/logout) into the
// localStorage("rolesPermissions") list that dynamicRoutingByModule and the
// side nav read. Returns the module list ([] on failure) — callers use the
// first entry as the post-switch landing route, exactly like the saga does.
export const refreshStoredRolesPermissions = async () => {
  try {
    const rolesAndPermissions = await getRolesAndPermissionsService();
    if (!rolesAndPermissions?.success) return [];
    const roles = rolesAndPermissions?.data?.map(
      (parentModule) => parentModule?.moduleKey
    );
    const subRoles = rolesAndPermissions?.data?.map((parentModule) =>
      parentModule?.subModule?.map((subModule) => subModule?.moduleKey)
    );
    const modules = [
      ...(roles || []),
      ...(subRoles || []).flat(),
      'settings',
      'logout',
    ].filter(Boolean);
    localStorage.setItem('rolesPermissions', JSON.stringify(modules));
    return modules;
  } catch (e) {
    return [];
  }
};

// Mirrors SettingsSaga's localStorage snapshot of the profile response.
// Best-effort: a failing profile call must not abort a seat switch.
export const persistProfileSnapshot = async () => {
  try {
    const userProfile = await profileApi();
    if (userProfile?.success) {
      localStorage.setItem('user', JSON.stringify(userProfile?.data));
      localStorage.setItem('countryCode', userProfile?.data?.countryCode);
      localStorage.setItem('ID', userProfile?.data?.id);
      localStorage.setItem('role', userProfile?.data?.companyType);
      localStorage.setItem('status', userProfile?.data?.status);
      localStorage.setItem('companyId', userProfile?.data?.companyId);
      localStorage.setItem('location', userProfile?.data?.location);
      localStorage.setItem('serviceRegion', userProfile?.data?.serviceRegion);
      localStorage.setItem('companyName', userProfile?.data?.companyName);
    }
  } catch (e) {
    // non-fatal
  }
};

// Put the super admin back on the platform seat: restore the stashed role-99
// token, point x-tenant back at that token's resident tenant (or none), and
// re-persist rolesPermissions/profile/branding for it. Returns false when no
// stash exists (caller should fall back to a clean logout).
export const restorePlatformSeat = async () => {
  localStorage.removeItem('actingAs');
  const platformToken = localStorage.getItem('platformToken');
  if (!platformToken) return false;

  localStorage.setItem('token', platformToken);
  localStorage.removeItem('platformToken');

  const payload = decodeJwt(platformToken);
  if (payload?.tenantId) {
    localStorage.setItem('tenantId', payload.tenantId);
  } else {
    localStorage.removeItem('tenantId');
  }
  localStorage.removeItem('tenantConfig');

  await refreshStoredRolesPermissions();
  await persistProfileSnapshot();
  try {
    const config = await loadTenantConfig();
    injectTheme(config);
  } catch (e) {
    // branding is cosmetic — never block the restore
  }
  return true;
};
